import { useEffect, useRef } from "react";
import { Segmented } from "antd";

import {
  DIRECTIONS,
  HOUR_VALUES,
  LEAD_PRESETS,
  MINUTE_PRESETS,
  OFFSET_PRESETS,
  directionOf,
  formatLeadAmount,
  formatOffsetAmount,
  isLeadTime,
  leadKey,
  signedOffset,
  withCurrentLead,
  withCurrentValue,
} from "./reminder-time-format";
import { MESSAGE_KINDS } from "../../modules/administration/plan-management/components/reminder-mapping";
import "./reminder-time.css";

/**
 * LF360-1896 — when a reminder fires, chosen in one place.
 *
 * Both kinds live behind one control: a fixed time of day, or an offset from
 * something the subject told us — "30 min before Wake up". They were two separate
 * sets of inputs before, which is why a list holding both needed a caption on
 * every row to say what it was.
 *
 * Deliberately its own component under src/components rather than part of the
 * screen that uses it: the activity modal is being redesigned around the same
 * picker next, and the two have to agree about what a reminder is. Nothing else
 * has been switched over yet — that redesign carries far more than this.
 */
const Column = ({ label, items, value, onSelect, disabled, isDisabled }) => {
  const column = useRef(null);
  const selectedRow = useRef(null);

  // Bring what is already chosen into view when the column appears. A column
  // scrolls, and "before" is ninety rows long — a reminder set to forty days
  // opens with its own answer far below the fold, which reads as nothing being
  // chosen at all.
  //
  // Once, on the way in. Doing it whenever the value changes would drag the list
  // under the cursor of somebody who has just clicked, and their choice is on
  // screen already by definition.
  useEffect(() => {
    const box = column.current;
    const row = selectedRow.current;

    if (!box || !row) { return; }

    // Measured against the column, not against offsetParent. offsetTop is
    // relative to the nearest positioned ancestor, which the column is not — so
    // it carries the height of everything above the column as well, and the
    // scroll overshoots by exactly that much. Seen in a browser: a reminder at
    // 10:00 scrolled the column to 14.
    //
    // Set on the column itself rather than by scrollIntoView, which also scrolls
    // whatever the popover happens to sit inside.
    const offsetInColumn =
      row.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop;

    box.scrollTop = Math.max(0, offsetInColumn - (box.clientHeight - row.offsetHeight) / 2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="reminder-time-column" role="listbox" aria-label={label} ref={column}>
      {items.map((item) => {
        const selected = item.value === value;

        return (
          <button
            key={String(item.value)}
            type="button"
            role="option"
            aria-selected={selected}
            disabled={disabled || Boolean(isDisabled && isDisabled(item.value))}
            ref={selected ? selectedRow : undefined}
            className={
              selected
                ? "reminder-time-column__row reminder-time-column__row--selected"
                : "reminder-time-column__row"
            }
            onClick={() => onSelect(item.value)}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
};

const ReminderTimePicker = ({
  reminder,
  onChange,
  // The project's time questions. With none of them an offset has nothing to
  // count from, so the whole relative half is withheld rather than offered and
  // then refused by the backend.
  triggerFields = [],
  disabled = false,
  // LF360-1881: only a triggered task may be announced before it is assigned —
  // it is the only one that knows its date in advance. Every other surface is
  // given two kinds, as before.
  allowLeadTime = false,
  // LF360-1940: the earliest a fixed time may be, in minutes since local
  // midnight. Set when a task is being assigned for today, where the backend
  // refuses a reminder that has already passed — the hours before it are shown
  // and not selectable, so the rule is visible rather than met with a 400.
  // Only the exact-time half: an offset and an announcement are counted from
  // something else and have no clock time to be early.
  minMinutes = null,
}) => {
  const isLead = isLeadTime(reminder);
  const isRelative = !isLead && reminder?.notificationType === "relative";
  // Offered when there is a time question to count from — and always when the
  // reminder already counts from one, whatever the list came back with. Without
  // the second half a reminder whose field has since been deleted, or whose list
  // simply failed to load, loses the tab it is currently on: the control shows
  // nothing selected and there is no way back to it.
  const canBeRelative = triggerFields.length > 0 || isRelative;

  const minutes = Number(reminder?.triggerOffsetMinutes) || 0;
  const amount = Math.abs(minutes);
  const direction = directionOf(minutes);

  const hour = Number.isInteger(reminder?.remindHour) ? reminder.remindHour : 9;
  const minute = Number.isInteger(reminder?.remindMinute) ? reminder.remindMinute : 0;

  // What the reminder said before it became an offset.
  //
  // A time of day and an offset are stored in the same two columns, so becoming
  // relative empties them — and the way back had nothing to read, inventing 09:00
  // for a reminder the admin had only meant to look at. Worse where the list
  // already holds 09:00: the change collides with it and is refused, and the
  // control springs back to Relative with nothing said. That is the bug as
  // reported — "it will not switch back".
  //
  // Kept for as long as the picker is open, which is the length of the mistake it
  // exists to undo. A reminder that was stored as an offset has no earlier time
  // to give back and starts from the same default it always did.
  const lastAbsolute = useRef({ hour, minute });

  // How far ahead of the assignment, when this is that kind of reminder. It
  // keeps a time of day of its own — "three days before, at nine" — so the hour
  // and minute columns are the same two the exact-time half shows.
  const lead = {
    value: Number(reminder?.value) > 0 ? Number(reminder.value) : 1,
    unit: reminder?.unit === "hours" ? "hours" : "days",
  };

  const setKind = (next) => {
    if (next === "relative") {
      // Only on the way out to an offset, which is the one move that empties the
      // two columns. Remembering on the way back would overwrite what is being
      // given back with the 09:00 the empty columns read as.
      lastAbsolute.current = { hour, minute };

      onChange({
        notificationType: "relative",
        triggerOffsetMinutes: 0,
        // Never left empty: a reminder naming no field is refused on save, and
        // the picker has a perfectly good answer when the project has only one.
        triggerSubjectProfileFieldId:
          reminder?.triggerSubjectProfileFieldId || triggerFields[0]?.value || null,
        remindHour: null,
        remindMinute: null,
        messageKind: MESSAGE_KINDS.TASK_DUE,
        value: null,
        unit: null,
      });
      return;
    }

    if (next === "lead") {
      // Keeps whatever time of day it had: a reminder moved to "before" is still
      // sent at an hour, and the one already on screen is the admin's own answer
      // to which.
      onChange({
        notificationType: "absolute",
        messageKind: MESSAGE_KINDS.TASK_UPCOMING,
        value: lead.value,
        unit: lead.unit,
        remindHour: hour,
        remindMinute: minute,
        triggerOffsetMinutes: 0,
        triggerSubjectProfileFieldId: null,
      });
      return;
    }

    onChange({
      notificationType: "absolute",
      remindHour: lastAbsolute.current.hour,
      remindMinute: lastAbsolute.current.minute,
      triggerOffsetMinutes: 0,
      messageKind: MESSAGE_KINDS.TASK_DUE,
      value: null,
      unit: null,
    });
  };

  const kindOptions = [
    { label: "Exact time", value: "absolute" },
    ...(canBeRelative ? [{ label: "Relative", value: "relative" }] : []),
    ...(allowLeadTime ? [{ label: "Before", value: "lead" }] : []),
  ];

  return (
    <div className="reminder-time-picker">
      {kindOptions.length > 1 && (
        <div className="reminder-time-picker__kind">
          <Segmented
            block
            className="lf-segmented"
            disabled={disabled}
            // eslint-disable-next-line no-nested-ternary
            value={isLead ? "lead" : isRelative ? "relative" : "absolute"}
            onChange={setKind}
            options={kindOptions}
          />
        </div>
      )}

      <div
        className={[
          "reminder-time-picker__columns",
          isRelative ? "reminder-time-picker__columns--relative" : "",
          isLead ? "reminder-time-picker__columns--lead" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {isLead ? (
          <>
            {/* How far ahead, then the hour and the minute it goes out at — the
                same two columns the exact-time half shows, because a reminder
                three days early is still sent at a time of day. */}
            <Column
              key="lead-amount"
              label="How long before"
              disabled={disabled}
              value={leadKey(lead)}
              onSelect={(next) => {
                const [value, unit] = next.split("|");

                onChange({ value: Number(value), unit });
              }}
              items={withCurrentLead(LEAD_PRESETS, lead).map((preset) => ({
                value: leadKey(preset),
                label: formatLeadAmount(preset.value, preset.unit),
              }))}
            />

            <Column
              key="lead-hour"
              label="Hour"
              disabled={disabled}
              value={hour}
              onSelect={(next) => onChange({ remindHour: next, remindMinute: minute })}
              items={HOUR_VALUES.map((value) => ({ value, label: String(value) }))}
            />

            <Column
              key="lead-minute"
              label="Minute"
              disabled={disabled}
              value={minute}
              onSelect={(next) => onChange({ remindHour: hour, remindMinute: next })}
              items={withCurrentValue(MINUTE_PRESETS, minute).map((value) => ({
                value,
                label: String(value).padStart(2, "0"),
              }))}
            />
          </>
        ) : isRelative ? (
          <>
            <Column
              key="relative-offset"
              label="Offset"
              disabled={disabled}
              value={amount}
              onSelect={(next) =>
                onChange({ triggerOffsetMinutes: signedOffset(next, direction) })
              }
              items={withCurrentValue(OFFSET_PRESETS, amount).map((value) => ({
                value,
                label: formatOffsetAmount(value),
              }))}
            />

            <Column
              key="relative-direction"
              label="Direction"
              disabled={disabled || amount === 0}
              value={direction}
              onSelect={(next) =>
                onChange({ triggerOffsetMinutes: signedOffset(amount, next) })
              }
              items={[
                { value: DIRECTIONS.BEFORE, label: "Before" },
                { value: DIRECTIONS.AFTER, label: "After" },
              ]}
            />

            <Column
              key="relative-field"
              label="Counted from"
              disabled={disabled}
              value={reminder?.triggerSubjectProfileFieldId || null}
              onSelect={(next) => onChange({ triggerSubjectProfileFieldId: next })}
              items={triggerFields}
            />
          </>
        ) : (
          <>
            <Column
              key="absolute-hour"
              label="Hour"
              disabled={disabled}
              value={hour}
              onSelect={(next) => onChange({ remindHour: next, remindMinute: minute })}
              items={HOUR_VALUES.map((value) => ({ value, label: String(value) }))}
              // An hour is out only once every minute in it has passed.
              isDisabled={(value) =>
                Number.isInteger(minMinutes) && value * 60 + 59 < minMinutes}
            />

            <Column
              key="absolute-minute"
              label="Minute"
              disabled={disabled}
              value={minute}
              onSelect={(next) => onChange({ remindHour: hour, remindMinute: next })}
              items={withCurrentValue(MINUTE_PRESETS, minute).map((value) => ({
                value,
                label: String(value).padStart(2, "0"),
              }))}
              isDisabled={(value) =>
                Number.isInteger(minMinutes) && hour * 60 + value < minMinutes}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default ReminderTimePicker;
